import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { learningKeys } from '@/entities/learning';
import { useSession } from '@/entities/session';
import { BudgetForm, ReminderToggle } from '@/features/budget';
import {
  AvatarPicker,
  ChangePasswordForm,
  EmailStatus,
  LanguageSelect,
  ProfileForm,
} from '@/features/profile';

import { getLearningPlan } from '@/shared/api/learning';
import { currentTimeZone } from '@/shared/lib/timeZone';
import { ErrorMessage } from '@/shared/ui';

import styles from './SettingsPage.module.css';

/** Profile, avatar, language and security of the signed-in user. */
export function SettingsPage(): React.JSX.Element | null {
  const { t } = useTranslation();
  const { data: user } = useSession();
  const plan = useQuery({
    queryKey: learningKeys.plan(30),
    queryFn: ({ signal }) => getLearningPlan({ timeZone: currentTimeZone(), signal }),
    enabled: Boolean(user),
  });

  if (!user) {
    return null;
  }

  return (
    <div className={styles.page}>
      <h1>{t('settings.title')}</h1>

      <section className={styles.section} aria-labelledby="settings-profile">
        <h2 id="settings-profile">{t('settings.profile')}</h2>
        <ProfileForm key={user.id} user={user} />
      </section>

      <section className={styles.section} aria-labelledby="settings-budget">
        <h2 id="settings-budget">{t('settings.budget')}</h2>
        <p className={styles.hint}>{t('settings.budgetHint')}</p>
        {plan.isError && <ErrorMessage error={plan.error} />}
        {plan.data && <BudgetForm settings={plan.data.settings} />}
        {plan.data && (
          <ReminderToggle
            enabled={plan.data.settings.remind_by_email}
            emailVerified={user.email_verified}
          />
        )}
      </section>

      <section className={styles.section} aria-labelledby="settings-avatar">
        <h2 id="settings-avatar">{t('settings.avatar')}</h2>
        <AvatarPicker user={user} />
      </section>

      <section className={styles.section} aria-labelledby="settings-language">
        <h2 id="settings-language">{t('settings.language')}</h2>
        <LanguageSelect user={user} labelledBy="settings-language" />
      </section>

      <section className={styles.section} aria-labelledby="settings-email">
        <h2 id="settings-email">{t('settings.email')}</h2>
        <EmailStatus user={user} />
      </section>

      <section className={styles.section} aria-labelledby="settings-security">
        <h2 id="settings-security">{t('settings.security')}</h2>
        <ChangePasswordForm user={user} />
      </section>
    </div>
  );
}
